import { ReactNode, Ref, createContext, useRef } from 'react';
import { useMergeRefs } from 'rooks';
import { css } from '@mildang/styled-system/css';
import { css as emotionCSS } from '@emotion/react';
import { Theme } from '@mildang/mui';
import useSize from '@react-hook/size';
import { commonScrollbarStyle } from 'styles/commonStyle';
import { token } from '@mildang/styled-system/tokens';

const headerStyle = css({
  gridColumn: '1 / -1',
});

const parentStyle = emotionCSS`
  display: grid;
  grid-template: auto 1fr / auto 1fr;
  height: var(--fullHeight, 100vh);
`;

const overflowStyle = (theme: Theme) => emotionCSS`
  overflow-y: auto;
  min-height: 0;
  display: grid;
`;
const sidebarStyle = ({
  theme,
  sidebarBorderRight,
}: {
  theme: Theme;
  sidebarBorderRight?: boolean;
}) => emotionCSS`
  position: relative;
  grid-column: 1 / 2;
  grid-template-columns: auto;
  ${overflowStyle(theme)}
  ${commonScrollbarStyle({ theme })};

  transition: ${theme.transitions.create(['margin'], {
    duration: theme.transitions.duration.complex,
  })};

  ${
    sidebarBorderRight &&
    emotionCSS`
    border-right: 1px solid ${token('colors.common.5')};
  `
  }
`;

const mainStyle = (theme: Theme) => emotionCSS`
  position: relative;
  grid-column: 2 / 3;
  ${overflowStyle(theme)}
`;

const rightSidebarStyle = (theme: Theme) => emotionCSS`
  position: relative;
  grid-column: 3 / -1;
  scrollbar-width: none;
  ${overflowStyle(theme)}
  ${commonScrollbarStyle({ theme })};
`;

export const HeaderSidebarLayoutClasses = {
  sidebar: 'MDuiHeaderSidebarLayout-sidebar',
  header: 'MDuiHeaderSidebarLayout-header',
  main: 'MDuiHeaderSidebarLayout-main',
  rightChildren: 'MDuiHeaderSidebarLayout-rightChildren',
};

export type SidebarToggleFn = (params: { source: 'programmatic' | 'user'; open?: boolean }) => void;

export interface HeaderSidebarLayoutProps {
  header?: ReactNode;
  sidebar?: ReactNode;
  children: ReactNode;
  className?: string;
  mainRef?: Ref<HTMLElement>;
  sidebarRef?: Ref<HTMLDivElement>;
  sidebarOpen?: boolean;
  onSidebarToggle?: SidebarToggleFn;
  controlled?: boolean;
  unmountSidebar?: boolean;
  sidebarBorderRight?: boolean;
  rightChildren?: ReactNode;
}

export const HeaderSidebarLayoutContext = createContext<{
  sidebarOpen: boolean;
  onSidebarToggle?: SidebarToggleFn;
}>({
  sidebarOpen: false,
  onSidebarToggle: ({ source = 'user', open }) => {},
});

const HeaderSidebarLayout = (props: HeaderSidebarLayoutProps) => {
  const {
    children,
    header,
    sidebar,
    className,
    mainRef = null,
    sidebarRef,
    sidebarOpen = false,
    onSidebarToggle,
    controlled = false,
    unmountSidebar = false,
    sidebarBorderRight = false,
    rightChildren,
  } = props;

  const target = useRef<HTMLDivElement>(null);
  const [width] = useSize(target.current);

  const setSidebarRef = useMergeRefs(target, sidebarRef);

  return (
    <HeaderSidebarLayoutContext.Provider value={{ sidebarOpen, onSidebarToggle }}>
      <div className={className} css={parentStyle}>
        <div className={`${HeaderSidebarLayoutClasses.header} ${headerStyle}`}>{header}</div>
        {!unmountSidebar && (
          <div
            className={HeaderSidebarLayoutClasses.sidebar}
            ref={setSidebarRef}
            css={(theme: Theme) => sidebarStyle({ theme, sidebarBorderRight })}
            style={{
              ...(controlled ? { marginLeft: sidebarOpen ? 0 : -width } : {}),
            }}
          >
            {sidebar}
          </div>
        )}
        <main className={HeaderSidebarLayoutClasses.main} css={mainStyle} ref={mainRef} tabIndex={-1}>
          {children}
        </main>
        {rightChildren && (
          <div className={HeaderSidebarLayoutClasses.rightChildren} css={rightSidebarStyle}>
            {rightChildren}
          </div>
        )}
      </div>
    </HeaderSidebarLayoutContext.Provider>
  );
};

export default HeaderSidebarLayout;
